/**
 * RegisterTimelogUseCase
 * Layer: application (imports from domain only)
 *
 * Centralizes timelog creation:
 * - Guards idEntity and idStructure before any persistence
 * - Sources id_device via IDeviceIdProvider (DI — keeps it testable)
 * - Builds a TimelogDraft with a fresh UUID localId
 * - Delegates persistence to ITimelogRepository
 *
 * Design: correcto-registro-de-timelogs-locales-y-remotos (D1, D2, D5)
 */
import { v4 as uuidv4 } from 'uuid';
import type { ITimelogRepository } from '../../domain/repositories/ITimelogRepository';
import type { IDeviceIdProvider } from '../../domain/ports/IDeviceIdProvider';
import type { Timelog } from '../../domain/entities/Timelog';
import type { TimelogDraft } from '../../domain/entities/TimelogDraft';
import type { DataUriPhoto } from '../../domain/valueObjects/DataUriPhoto';
import {
  MissingEmployeeStructureError,
  InvalidEmployeeEntityError,
} from '../../domain/errors/TimelogErrors';
import { getMarkingTimezone } from '@/shared/utils/timezone';

export interface RegisterTimelogInput {
  employee: { idEntity: number; idBiometric?: number | null };
  accessType: 0 | 1 | 2;  // 0 = automatic (backend decides), 1 = entrada manual, 2 = salida manual
  photo?: DataUriPhoto;
  verificationMode: number;
  isOffline: boolean;
  /** Resolved by caller from selectedRole.structures[0], guard > 0 */
  idStructure: number;
  /**
   * Structure code resolved by caller from `selectedRole.structuresInfo`
   * (see `resolveStructureCode` in the AccessRole domain entity). Persisted
   * on the draft so the repository never has to recompute it at read time
   * against a possibly-stale employee cache. `null`/omitted when unresolved.
   */
  structureCode?: string | null;
  /** Device id string override; falls back to IDeviceIdProvider when omitted */
  idDevice?: string | null;
  /** GPS coordinates captured at identification time; defaults to {0,0} if absent */
  coordinates?: { lat: number; lng: number } | undefined;
  /** Source identifier propagated to TimelogDraft.source (e.g. 'clock-web'). */
  source?: string | null;
  /** Reverse-geocoded location resolved from coordinates at identification time. */
  location?: string | null;
  /** Minimum seconds between consecutive timelogs of the same accessType (90-300, default 90). Injected from quality settings. */
  consecutiveMinSeconds?: number;
}

export class RegisterTimelogUseCase {
  constructor(
    private readonly repository: ITimelogRepository,
    private readonly deviceProvider: IDeviceIdProvider,
  ) { }

  async execute(
    input: RegisterTimelogInput,
  ): Promise<Timelog | { virtualSuccess: true; localId: string }> {
    // D5 guard — idStructure must be > 0
    if (input.idStructure <= 0) {
      throw new MissingEmployeeStructureError();
    }

    // D2 guard — idEntity must be > 0 (never use session fallback)
    if (input.employee.idEntity <= 0) {
      throw new InvalidEmployeeEntityError();
    }

    // Resolve id_device — input takes precedence; falls back to provider; null as last resort
    // Per spec: input.idDevice ?? deviceProvider.getDeviceId() ?? null
    // Device IDs are UUID/serial strings — no numeric conversion needed
    const idDevice: string | null =
      input.idDevice ?? this.deviceProvider.getDeviceId() ?? null;
    const serialDevice: string | null = this.deviceProvider.getSerial() ?? null;

    // NEW (v2): Get MARKING timezone from Dexie (structure's timezone from selected profile)
    // This ensures timelogs use the structure's timezone, not the user's timezone
    // Fallback chain: marking timezone → device timezone → browser timezone
    // OLD (v1): timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    const timezone = await getMarkingTimezone();

    const draft: TimelogDraft = {
      localId: uuidv4(),
      idEntity: input.employee.idEntity,
      idStructure: input.idStructure,
      structureCode: input.structureCode ?? null,
      accessType: input.accessType,
      verificationMode: input.verificationMode,
      photo: input.photo?.value ?? null,
      idDevice,
      serialDevice,
      idBiometric: input.employee.idBiometric ?? null,
      coordinates: input.coordinates ?? { lat: 0, lng: 0 },
      location: input.location ?? null,
      // timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, //Using timezone Navegator
      timezone,
      datetime: new Date().toISOString(),
      isOffline: input.isOffline,
      source: input.source ?? null,
      syncStatus: 'pending',
      serverId: null,
      attempts: 0,
      lastError: null,
      createdAt: Date.now(),
    };

    return this.repository.create(draft, input.consecutiveMinSeconds);
  }
}
